import { Boxes } from 'lucide-react';

import type { CameraPose } from '../../engine/camera-playback';
import type { ScriptElement, ThreeDContent } from '../../schema';
import { useElementCameraPlayback } from '../camera-playback-context';

import { ModelViewerCanvas } from './ModelViewerCanvas';

function asThreeD(content: unknown): ThreeDContent {
  if (typeof content === 'string') return { src: content };
  if (content && typeof content === 'object') return content as ThreeDContent;
  return { src: '' };
}

/** The element's authored "home" pose, or null when none was captured. */
function defaultPoseOf(data: ThreeDContent): CameraPose | null {
  if (!data.defaultCameraOrbit) return null;
  return {
    cameraOrbit: data.defaultCameraOrbit,
    cameraTarget: data.defaultCameraTarget,
    fieldOfView: data.fieldOfView,
  };
}

interface Props {
  element: ScriptElement;
  /** When `true`, the viewer fills its parent (the wrapper sets the box size). */
  fill?: boolean;
}

export function ThreeDElement({ element, fill }: Props) {
  const data = asThreeD(element.content);
  // During playback this element may be driven by a camera keyframe timeline
  // (via CameraPlaybackProvider); otherwise it's user-controlled / auto-rotate.
  const playback = useElementCameraPlayback(element.id);

  const w = element.style?.width as number | string | undefined;
  const h = element.style?.height as number | string | undefined;

  const boxStyle: React.CSSProperties = {
    overflow: 'hidden',
    borderRadius: 6,
    ...(fill
      ? { width: '100%', height: '100%' }
      : {
          width: w !== undefined ? (typeof w === 'number' ? `${w}px` : w) : '100%',
          height: h !== undefined ? (typeof h === 'number' ? `${h}px` : h) : undefined,
          maxWidth: '100%',
          ...(h === undefined ? { aspectRatio: '16 / 9' } : {}),
        }),
  };

  if (!data.src) {
    return (
      <div
        style={{
          ...boxStyle,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          background: 'linear-gradient(135deg, #e0e7ff, #f3e8ff)',
          color: '#4338ca',
        }}
      >
        <Boxes size={28} />
        <span style={{ fontSize: 12, fontWeight: 500 }}>3D model</span>
      </div>
    );
  }

  const inPlayback = playback != null;
  const pose = inPlayback ? playback.pose : defaultPoseOf(data);
  // interpolation-decay IS the camera damper time constant (larger = slower).
  const interpolationDecay = inPlayback ? Math.min(2600, Math.max(60, playback.transitionMs)) : 100;

  const background = data.transparentBackground
    ? 'transparent'
    : 'linear-gradient(135deg, #f1f5f9, #e2e8f0)';
  const fadeMask = data.fadeEdges
    ? 'radial-gradient(ellipse 78% 78% at 50% 50%, #000 58%, transparent 92%)'
    : undefined;

  return (
    <div
      style={{
        position: 'relative',
        ...boxStyle,
        background,
        ...(fadeMask ? { maskImage: fadeMask, WebkitMaskImage: fadeMask } : {}),
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {data.groundShadow !== false && (
        <div
          style={{
            position: 'absolute',
            left: '14%',
            right: '14%',
            bottom: '5%',
            height: '14%',
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0.32) 0%, rgba(0,0,0,0.18) 38%, rgba(0,0,0,0) 72%)',
            filter: 'blur(3px)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      )}
      <ModelViewerCanvas
        src={data.src}
        poster={data.poster}
        alt={data.alt}
        exposure={data.exposure}
        autoRotate={inPlayback ? false : data.autoRotate !== false}
        cameraControls={data.lockCamera ? false : inPlayback ? false : data.cameraControls !== false}
        pose={pose}
        interpolationDecay={interpolationDecay}
        labels={inPlayback ? playback.labels : undefined}
        style={{ width: '100%', height: '100%', backgroundColor: 'transparent' }}
      />
    </div>
  );
}
